import React from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent,
  CircularProgress,
  Avatar,
  Stack,
  Chip
} from '@mui/material';
import { 
  Work, 
  School, 
  CheckCircle, 
  Pending,
  Article,
  BusinessCenter
} from '@mui/icons-material';

const DashboardSummary = ({ candidateData, applicationData, profileCompletion }) => {
  // Count applications by status
  const statusCounts = applicationData.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});

  // Summary cards data
  const summaryCards = [
    {
      title: "Profile Completion",
      value: `${profileCompletion.overall}%`,
      icon: <CheckCircle color="primary" />,
      color: profileCompletion.overall > 80 ? "success.light" : 
             profileCompletion.overall > 40 ? "warning.light" : "error.light",
      subtitle: profileCompletion.overall > 80 ? "Well done!" : 
                profileCompletion.overall > 40 ? "Keep going!" : "Start updating"
    },
    {
      title: "Total Applications",
      value: applicationData.length,
      icon: <Article color="info" />,
      color: "info.light",
      subtitle: "Job applications"
    },
    {
      title: "Experience",
      value: candidateData?.experience_yrs ? `${candidateData.experience_yrs}+ years` : "Not specified",
      icon: <Work color="secondary" />,
      color: "secondary.light",
      subtitle: `${candidateData?.currentRole || 'Professional'}`
    },
    {
      title: "Education",
      value: candidateData?.education && candidateData.education.length > 0 ? 
             candidateData.education[0].degree : "Not specified",
      icon: <School color="success" />,
      color: "success.light",
      subtitle: candidateData?.education && candidateData.education.length > 0 ? 
                candidateData.education[0].institution : "Add your education"
    }
  ];

  // Application status breakdown
  const statusList = [
    { name: "Applied", value: statusCounts["Applied"] || 0, color: "info.main" },
    { name: "Screening", value: statusCounts["Screening"] || 0, color: "primary.main" },
    { name: "Interview", value: statusCounts["Interview"] || 0, color: "secondary.main" },
    { name: "Offer", value: statusCounts["Offer"] || 0, color: "success.main" },
    { name: "Rejected", value: statusCounts["Rejected"] || 0, color: "error.main" }
  ];

  return (
    <Grid container spacing={3}>
      {/* Summary Cards */}
      {summaryCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Paper
            elevation={1}
            className="dashboard-card stat-card"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              backgroundImage: `linear-gradient(135deg, white 85%, ${card.color} 85%)`,
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: 4
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="h5" component="div" gutterBottom fontWeight="bold">
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </Box>
              <Avatar 
                sx={{ 
                  bgcolor: card.color, 
                  width: 40, 
                  height: 40,
                }}
              >
                {card.icon}
              </Avatar>
            </Box>
          </Paper>
        </Grid>
      ))}

      {/* Applications Status Overview */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', pb: 1, borderBottom: '1px solid #eee' }} className="section-header">
              <BusinessCenter sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
              Application Status Overview
            </Typography>
          
          <Box sx={{ mt: 2 }}>
            {applicationData.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                You haven't applied to any jobs yet. Start exploring opportunities!
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {statusList.map((status, index) => (
                  <Grid item xs={12} sm={status.value > 0 ? 4 : 2} key={index}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        bgcolor: status.value > 0 ? `${status.color}10` : 'transparent',
                        borderColor: status.value > 0 ? status.color : 'divider'
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" color={status.value > 0 ? status.color : 'text.secondary'}>
                          {status.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {status.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Paper>
      </Grid>

      {/* Profile Highlight */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', pb: 1, borderBottom: '1px solid #eee' }}>
            <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
            Profile Highlights
          </Typography>
          
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {candidateData?.skills && candidateData.skills.slice(0, 5).map((skill, index) => (
              <Chip 
                key={index}
                label={skill}
                size="medium"                      color={index < 3 ? "primary" : "default"}
                      variant={index < 3 ? "filled" : "outlined"}
                      className="skill-chip"
              />
            ))}
            
            {(!candidateData?.skills || candidateData.skills.length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Add skills to your profile to highlight your expertise
              </Typography>
            )}
            
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Profile strength
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress 
                  variant="determinate" 
                  value={profileCompletion.overall} 
                  size={38}
                  thickness={4}
                  sx={{ 
                    color: profileCompletion.overall > 80 ? "success.main" : 
                           profileCompletion.overall > 40 ? "warning.main" : "error.main"
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" component="div" color="text.secondary">
                    {`${profileCompletion.overall}%`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DashboardSummary;
